import React from 'react';
import { Calendar, MapPin, Plane, Users } from 'lucide-react';
import { AvatarDisplay } from '../../utils/avatars';
import { getThumbUrl } from '../../utils/travelImages';
import { SECTION_THEME } from '../../utils/sectionTheme';
import { getCardImageBadgeStyle } from '../../utils/cardBadgeStyles';
import { TRAVEL_BUDGET_LABELS, TRAVEL_GENDER_LABELS } from '../../utils/travelPostLabels';

const formatDateRange = (start, end) => {
  if (!start) return '日期未定';
  const startLabel = new Date(start).toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric' });
  if (!end) return startLabel;
  const endLabel = new Date(end).toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric' });
  return `${startLabel}－${endLabel}`;
};

export default function TravelPostCard({ post, onOpen, onOrganizerOpen, isPreview = false }) {
  const approvedCompanions = (post.travel_applications || [])
    .filter(application => application.status === 'approved' && application.applicant)
    .map(application => application.applicant);
  const remainingSlots = post.max_companions
    ? Math.max(post.max_companions - approvedCompanions.length, 0)
    : null;
  const isRecommended = (remainingSlots !== null && remainingSlots > 0 && remainingSlots <= 1)
    || ['premium', 'high'].includes(post.budget_range);
  const countries = post.countries?.length > 0 ? post.countries : (post.country ? [post.country] : []);
  const coverUrl = post.image_urls?.[0];
  const organizer = post.profiles || post.organizer || {};

  const handleOpen = () => {
    if (onOpen) onOpen(post);
  };

  return (
    <article
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={onOpen ? `查看旅伴行程：${post.destination || '未命名旅程'}` : undefined}
      onClick={handleOpen}
      onKeyDown={(event) => {
        if (onOpen && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          handleOpen();
        }
      }}
      style={{
        width: '100%',
        overflow: 'hidden',
        background: '#ffffff',
        border: '1px solid rgba(226, 232, 240, 0.9)',
        borderRadius: '20px',
        boxShadow: '0 12px 32px rgba(15, 23, 42, 0.09)',
        cursor: onOpen ? 'pointer' : 'default',
        outline: 'none'
      }}
    >
      <div style={{
        position: 'relative',
        minHeight: isPreview ? '250px' : '220px',
        overflow: 'hidden',
        background: 'linear-gradient(145deg, #315f91 0%, #6a9fd3 54%, #b9d7ef 100%)'
      }}>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Plane size={42} color="rgba(255,255,255,0.76)" strokeWidth={1.5} />
        </div>
        {coverUrl && (
          <img
            src={getThumbUrl(coverUrl)}
            onError={(event) => {
              const image = event.currentTarget;
              if (!image.dataset.originalAttempted) {
                image.dataset.originalAttempted = 'true';
                image.src = coverUrl;
              } else {
                image.style.display = 'none';
              }
            }}
            alt=""
            loading="lazy"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}

        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(2, 8, 23, 0.1) 0%, rgba(2, 8, 23, 0.04) 30%, rgba(2, 8, 23, 0.86) 100%)' }} />

        <div style={{ position: 'absolute', top: '12px', left: '12px', right: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
            {isRecommended && (
              <span style={{ padding: '5px 10px', color: '#ffffff', background: SECTION_THEME.travel.accent, borderRadius: '999px', boxShadow: '0 4px 12px rgba(15, 23, 42, 0.2)', fontSize: '11px', fontWeight: '800' }}>
                推薦
              </span>
            )}
            {countries.slice(0, 2).map(country => (
              <span key={country} style={{ maxWidth: '104px', padding: '5px 10px', overflow: 'hidden', color: '#ffffff', background: 'rgba(15, 23, 42, 0.48)', border: '1px solid rgba(255, 255, 255, 0.28)', borderRadius: '999px', backdropFilter: 'blur(8px)', fontSize: '11px', fontWeight: '700', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {country}
              </span>
            ))}
          </div>
          {post.budget_range && (
            <span
              data-testid="travel-budget-badge"
              style={{ ...getCardImageBadgeStyle(SECTION_THEME.sports.accent), flexShrink: 0 }}
            >
              {TRAVEL_BUDGET_LABELS[post.budget_range] || post.budget_range}
            </span>
          )}
        </div>

        <div style={{ position: 'absolute', left: '16px', right: '16px', bottom: '15px', color: '#ffffff' }}>
          <h2 style={{ margin: '0 0 6px', overflow: 'hidden', fontSize: '20px', fontWeight: '850', letterSpacing: '-0.025em', lineHeight: '1.3', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textShadow: '0 2px 10px rgba(0,0,0,0.35)' }}>
            {post.destination || '未命名旅程'}
          </h2>
          {post.description && (
            <p style={{ margin: '0 0 10px', overflow: 'hidden', color: 'rgba(255,255,255,0.86)', fontSize: '12px', lineHeight: '1.45', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {post.description}
            </p>
          )}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '7px 12px', color: 'rgba(255,255,255,0.9)', fontSize: '11px', fontWeight: '650' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <Calendar size={13} />
              {formatDateRange(post.trip_start_date, post.trip_end_date)}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <Users size={13} />
              找{post.max_companions || 1}人 · {TRAVEL_GENDER_LABELS[post.gender_pref] || '性別不限'}
            </span>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minHeight: '58px', padding: '10px 14px' }}>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            if (onOrganizerOpen) onOrganizerOpen(post);
          }}
          disabled={!onOrganizerOpen}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, padding: 0, color: '#475569', background: 'transparent', border: 'none', fontFamily: 'inherit', cursor: onOrganizerOpen ? 'pointer' : 'default', textAlign: 'left' }}
        >
          <span style={{ flexShrink: 0, width: '32px', height: '32px', overflow: 'hidden', background: '#eef4f9', border: '2px solid #ffffff', borderRadius: '50%', boxShadow: '0 2px 8px rgba(15, 23, 42, 0.08)' }}>
            <AvatarDisplay avatarId={organizer.avatar} size={32} style={{ width: '100%', height: '100%', border: 'none', boxShadow: 'none' }} />
          </span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', color: '#94a3b8', fontSize: '10px', fontWeight: '600' }}>主辦人</span>
            <span style={{ display: 'block', overflow: 'hidden', color: '#334155', fontSize: '12px', fontWeight: '750', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {organizer.name || '尚未設定'}
            </span>
          </span>
        </button>

        <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', paddingLeft: '8px' }}>
          {approvedCompanions.slice(0, 3).map((companion, index) => (
            <span key={companion.id || index} style={{ width: '27px', height: '27px', marginLeft: index === 0 ? 0 : '-8px', overflow: 'hidden', background: '#ffffff', border: '2px solid #ffffff', borderRadius: '50%', boxShadow: '0 1px 5px rgba(15, 23, 42, 0.12)' }}>
              <AvatarDisplay avatarId={companion.avatar} size={27} style={{ width: '100%', height: '100%', border: 'none', boxShadow: 'none' }} />
            </span>
          ))}
          {approvedCompanions.length > 3 && (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '27px', height: '27px', marginLeft: '-8px', color: '#475569', background: '#e2e8f0', border: '2px solid #ffffff', borderRadius: '50%', fontSize: '9px', fontWeight: '800' }}>
              +{approvedCompanions.length - 3}
            </span>
          )}
          {approvedCompanions.length === 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#94a3b8', fontSize: '11px', fontWeight: '600' }}>
              <MapPin size={12} /> 等你加入
            </span>
          )}
        </div>
      </div>
    </article>
  );
}
